/**
 * Normalizers applied before hashing a fingerprint part.
 *
 * The reference site normalizes parts before hashing "so that some minor changes
 * do not necessarily produce a completely different fingerprint part". Without
 * this, a 0.5px difference in a measured metric would flip the part and destroy
 * the ability to compare two runs.
 *
 * Every normalizer here is documented with the reason it exists, so a reviewer
 * can tell deliberate coarsening from an accident.
 */

/** Round to N significant digits, so float noise below measurement precision is dropped. */
export function sig(value: number, digits = 3): number | null {
  if (!Number.isFinite(value)) return null;
  if (value === 0) return 0;
  // Scale by 10^(digits - 1 - exponent), round, scale back.
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const factor = 10 ** (digits - 1 - exponent);
  return Number((Math.round(value * factor) / factor).toPrecision(digits));
}

/**
 * Bucket screen dimensions. A window resized by 1px should not change the
 * `screen` part; a genuinely different display should.
 */
export function bucketDimension(px: number, step = 64): number {
  if (!Number.isFinite(px) || px <= 0) return -1;
  return Math.round(px / step) * step;
}

/** Boolean sets (capability matrices) hash stably only when sorted. */
export function sortedFlags<T extends string>(flags: Iterable<T>): T[] {
  return [...flags].sort();
}

/**
 * Collapse a WebGL / WebGPU renderer string to a coarse family.
 *
 * Renderer strings churn constantly (driver versions, ANGLE revisions), so the
 * part hashes the family ("apple-metal", "swiftshader", "llvmpipe") rather than
 * the full string. The full string is still displayed in the signal list.
 */
export function rendererFamily(renderer: string): string {
  const r = renderer.toLowerCase();
  if (r.includes('swiftshader')) return 'swiftshader';
  if (r.includes('llvmpipe')) return 'llvmpipe';
  if (r.includes('softpipe')) return 'softpipe';
  if (r.includes('nvidia')) return 'nvidia';
  if (r.includes('amd') || r.includes('ati')) return 'amd';
  if (r.includes('intel')) return 'intel';
  if (r.includes('apple')) return 'apple';
  if (r.includes('adreno')) return 'adreno';
  if (r.includes('mali')) return 'mali';
  if (r.includes('powervr')) return 'powervr';
  if (r.includes('microsoft basic')) return 'warp';
  if (r.includes('mesa')) return 'mesa';
  if (r.includes('mesa offscreen')) return 'llvmpipe';
  return 'other';
}

/** Coarse OS family from a renderer/GL string, for coherence checks. */
export function glReportedOs(renderer: string, vendor: string): string {
  const r = renderer.toLowerCase();
  const v = vendor.toLowerCase();
  if (r.includes('apple') || v.includes('apple')) return 'macOS';
  if (r.includes('nvidia') && r.includes('direct3d')) return 'Windows';
  if (r.includes('adreno') || r.includes('mali') || r.includes('powervr')) return 'mobile';
  if (r.includes('llvmpipe') || r.includes('swiftshader') || r.includes('mesa')) return 'Linux/VM?';
  return 'unknown';
}

/**
 * Normalize a measured duration / latency value in ms.
 * Bucketed to reduce jitter; see README on why parts are normalized.
 */
export function bucketMs(ms: number): number | null {
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1) return 0;
  return Math.round(Math.log2(ms) * 2) / 2; // ~0.71x buckets
}

/** Normalize an object by dropping keys whose value is null/undefined. */
export function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}
